import jwt     from 'jsonwebtoken';
import isEmpty  from 'lodash/isEmpty';
import AppConfig   from '../config/AppConfig';
import { prtErr, prtStart, prtMsg, prtVar }    from './prt';

//*******************************************************************************************
// This class makes use of JWT to generate a signed token given a key and payload. The token
// is then substituted for the {token} pattern in the urlString provided.
//
// Inputs(user provided):
// urlString: sample url string is https://invite.pitchinclub.com/{token}. The {token} pattern will be
//            replaced with the actual signed token.
// payload:   This is an object that must contain a uid and eid property as so:  {uid: "userid" eid: "entity"}.
//            Other custom properties are allowed, but these two are minimally required in order associate
//            token with a user id and primary entity id.
// saveFunction: If this function is passed into the constructor, it will be used to save uid, eid,
//            and token to the database. The caller decides how to implement the save and whether
//            they will be tracking expiration or limiting number of times link can be accessed. When
//            function is defined, it is expected tak 2 parameters: payload and tokenizedUrl.
//
// Inputs(sytem provided):
// Key:       This is key used to sign and decode token. User does need to provide this as it is
//            being sourced from the environment variable.
//
// Note that there 2 class modes:
//           1) Generating token and tokenized url: In this mode you must provide the
//              user provided inputs described above to the constructor
//           2) Validating token and getting payload: In this mode no arguments are
//              required when calling "new". The only inputs required are the system provided inputs.

// Process:
// There are usage examples at the bottom of this file that are commented out.//
//
// Output:    tokenizedUrl
//            If a saveFunction is provided, it will also save uid, eid and token to database
//*******************************************************************************************

const FNAME = "TokenizedUrl.js"
const TRACE = false;

/* Set the Signature key from the environment variable set by config */
const SIG_KEY   = AppConfig("invitationkey");

class TokenizedUrl {

    /* Define token substitution pattern: this is where to substitute the actual token in the url string */
    TOKEN_REPLACEMENT_PATTERN = /{token}/gi;

    /*   */
    constructor (urlString, payload, saveFunction) {
        this.urlString    = urlString || "";
        this.payload      = payload || {};
        this.saveFunction = saveFunction || null;
        this.signatureKey = SIG_KEY;   /* initialize the signing key from evn variable */
        this.tokenizedUrl = arguments.length > 0 ? this._buildTokenizedUrl() : ""; /* run _buildTokenizedUrl in class mode #1  only */
        this.token        = "";
        this.decodedPayload  = {};   /* set by isValidToken method */
    }


    //*************************************************************************************
    // Following are methods used in class mode #1.  That means methods below are used to sign a JWT token
    // given a payload and signing key. After signing token it substitutes the {token} pattern
    // in the urlString provided with generated token, so it can be shared externally with a user.
    //*************************************************************************************

    /* This method will be called by the constructor at instantiation time and by setPayLoad */
    _buildTokenizedUrl = () => {
        TRACE && prtStart(FNAME, "_buildTokenizedUrl")
        TRACE && prtVar("this.payload:", JSON.stringify(this.payload));
        /* initialize possible payload error messages */
        let payloadError = null;
        let payloadError1 = "No payload object was provided in the TokenizedUrl constructor";
        let payloadError2 = "The uid property was omitted on the payload object passed to the TokenizedUrl constructor";
        let payloadError3 = "The eid property was omitted on the payload object passed to the TokenizedUrl constructor";

        /* check that payload has been provided and if so that it minimally has a uid and eid property */
        if (isEmpty(this.payload)) {
          payloadError = payloadError1;
        } else if (!this.payload.uid)   {
          payloadError = payloadError2;
        } else if (!this.payload.eid)  {
          payloadError = payloadError3;
        }
        
        /* If no payload issues, generate token and return tokenized url string, otherise print error msg and return empty string*/
        if (!payloadError) {

          this.token = jwt.sign(this.payload, this.signatureKey);                /* construct token */
          return this.urlString.replace(this.TOKEN_REPLACEMENT_PATTERN, this.token);

          /* if client passes in a function to save token into the database, call it here */
          if (this.saveTokenizedUrl) { 
             TRACE && prtMsg("Calling function to save tokenizedUrl in database");
             saveTokenizedUrl(); 
          }

        } else {
          prtErr(`FN: ${FNAME} - ${payloadError}`)
          return "";
        }
    }

    // *********************************************************************
    // Setter methods
    // *********************************************************************

    setUrlString = (urlString) => {
       this.urlString = urlString;
    }

    /* when ever setting a new payload, build a new tokenized url using existing urlString passed to constructor */
    setPayload = (payload) => {
       this.payload = payload;
       this.tokenizedUrl = this._buildTokenizedUrl();
    }

    // *********************************************************************
    // Getter methods
    // *********************************************************************
    /* Return the tokenizedUrl */
    get = () => {
        return this.tokenizedUrl;
    }

    getToken = () => {
        return this.token;
    }

    /* Prints tokenizedUrl to console.log */
    toString = () => {
        console.log(this.tokenizedUrl);
    }

    // *********************************************************************
    // Setter methods
    // *********************************************************************
   

    /* call the save function with the payload and the tokenizedUrl */
    save = () => {
        this.saveFunction(this.payload, this.token)
    }

    //*************************************************************************************
    // Following methods are in class mode #2. That means these methods are used after you
    // received a url request and pulled off the token from the request.
    //*************************************************************************************

    /* validate token provided on the url request */
    isValidToken = (token) => {
        let tokenValid = false;

        try {
               this.decodedPayload = jwt.verify(token, this.signatureKey);
               tokenValid = true;
        } catch(e) {
               prtErr(`${FNAME} - "URI token is not valid`)
        }
        return tokenValid;
    }

    /* This method is used to get the payload off a token you received from a url
       after making sure token is valid */
    getPayload = () => {
        return this.decodedPayload;
    }
} /* end of class declaration */

export default TokenizedUrl;

// **************************************************************************************************
// Sample Usage#1. When using it for one a time tokenizedUrl, you can pass in all parameters
// at once when you instantiate object, then get TokenizedUrl.
// **************************************************************************************************

// /* create your save function to save uid, eid, and token to database .
//    The saveFunction would normally be imported from an external file and then passed into TokenizedUrl
//    Note, the payload and the token will be provided by the class itself to the function.
//    They are shown here to basically document requirements of save the function */
// let saveFunction = (payload, token) => {      /* uid and eid are pulled from  payload object */
//     console.log("Saving Tokenized URL to database")
//     console.log("this is payload used ",  payload)
// }
// /* create your sample payload */
// let samplePayload = {  uid: "123456", eid: "997755"};
//
// /* create an instance of the Tokenized Url Class */
// let tokenizedUrl = new TokenizedUrl("http://www.abc.com/{token}", samplePayload, saveFunction  );
//
// /* check no problem get the tokenized url */
// if (!tokenizedUrl.get()) {
//    console.log("Problem constructing tokenizedUrl. Check error log")
// } else {
//    console.log(tokenizedUrl.get())
// }
//
// /* save it to the database with function you provide to  TokenizedUrl */
// tokenizedUrl.save();

// **************************************************************************************************
// Sample Usage#2 using object recursively. For instance, sending an invitation url to different
// users. Firstly you should instantiate instance with the 3 parameters where 3rd is optional.
// Retrieve tokenizedUrl from initial instance, then you can call setPayLoad for each individual user.
// This will allow you to generate a unique user token for the same urlString initially provided.
// NOTE: even if you plan to use this in a loop, it is recommended to pass all 3 paramters (3rd optional)
// so you can reuse urlString and the saveFunction for the other users in your list.
// **************************************************************************************************

// /* create your sample payload */
// let samplePayload1 = {  uid: "123456", eid: "997755" };
//
// /* note, the payload and the token will be provide by the class itself.
//    They are shown here to basically document requirements of save function */
// let saveFunction = (payload, token) => {  /* uid and eid are pulled from  payload object */
//     console.log("Saving Tokenized URL to database")
//     console.log("this is payload used ",  payload)
// }
//
// /* Build the first tokenizedUrl by calling new and passing all three parameters to constructor */
// let tokenizedUrl = new TokenizedUrl("http://www.abc.com/{token}", samplePayload1, saveFunction);
// tokenizedUrl.save();                      /* this will set payload, generate new tokenizedUrl, and initialize saveFunction  */
// console.log(tokenizedUrl.get())
//
//
// /* assuming you are using same url for different users, you may use same instance and setPayload and get tokenized url */
// let samplePayload2 = {  uid: "88888", eid: "88888888" };
// tokenizedUrl.setPayload(samplePayload2);  /* this will set payload and generate new tokenizedUrl */
// tokenizedUrl.save();                      /* will save to database using function passed to constructor */
// console.log(tokenizedUrl.get())
//
//
// /* get 3rd */
// let samplePayload3 = {  uid: "1111111", eid: "222222" };
// tokenizedUrl.setPayload(samplePayload3)    /* this will set payload and generate new tokenizedUrl */
// tokenizedUrl.save();                        /* will save to database using function passed to constructor */
// console.log(tokenizedUrl.get())

// **************************************************************************************************
// Sample Usage#3 use class to validate token you received from a url request and extract the payload
// **************************************************************************************************

// To test this functionality you need run use case #1 first in order to generate a token.
// Uncomment use case#1 first, run it and copy token.
// Then add comments back into use case #1 and comment out this use case.

// let tokenizedUrl = new TokenizedUrl();
// if (tokenizedUrl.isValidToken("eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1aWQiOiIxMjM0NTYiLCJlaWQiOiI5OTc3NTUiLCJpYXQiOjE1OTE0NzQzMDF9.2ZJot0qCZcW3rlGnTEVwDySR5hrJZYDeqxjAg17gw2o")) {
//     console.log("This is payload ", tokenizedUrl.getPayload())
// }
// **************************************************************************************************
// Sample Usage#4 use case; Use TokenizedUrl class as a simple TokenUtil class that lets
// you create a token given a payload
// **************************************************************************************************

// let tokenUtil  = new TokenizedUrl();
// tokenUtil.setPayload({uid: 'testuid', eid: 'testuid'})
// let token = tokenUtil.getToken();
// console.log("This is token generated from payload", token)



// WEBPACK FOOTER //
// ./src/utils/TokenizedUrl.js